import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchLeadList,
  addLeadToCampaign,
  removeLeadFromCampaign,
  Lead,
} from "../../services/api";
import {
  HiOutlineX,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineSearch,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";

interface CampaignLeadAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
  currentLeadAccess?: number[]; // Array of lead IDs that currently have access
}

const CampaignLeadAccessModal: React.FC<CampaignLeadAccessModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  campaignName,
  currentLeadAccess = [],
}) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localLeadAccess, setLocalLeadAccess] = useState<number[]>(currentLeadAccess);

  // Update local state when currentLeadAccess prop changes
  useEffect(() => {
    setLocalLeadAccess(currentLeadAccess);
  }, [currentLeadAccess]);

  // Fetch all leads
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', 'all'],
    queryFn: async () => {
      const result = await fetchLeadList(100, 0); // Get more leads for selection
      return result;
    },
    enabled: isOpen,
  });

  const leads = leadsData?.data || [];

  // Filter leads based on search query
  const filteredLeads = leads.filter((lead) =>
    lead.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.phone.includes(searchQuery)
  );

  // Separate leads with and without access using local state
  const leadsWithAccess = filteredLeads.filter((lead) =>
    localLeadAccess.includes(lead.id)
  );
  const leadsWithoutAccess = filteredLeads.filter((lead) =>
    !localLeadAccess.includes(lead.id)
  );

  const handleAddLead = async (leadId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await addLeadToCampaign(campaignId, leadId);
      if (result.success) {
        // Update local state immediately for better UX
        setLocalLeadAccess(prev => [...prev, leadId]);
        // Refresh campaigns data
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      } else {
        setError(result.error || "Failed to add lead to campaign");
      }
    } catch (err: any) {
      setError(err.message || "Failed to add lead to campaign");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveLead = async (leadId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await removeLeadFromCampaign(campaignId, leadId);
      if (result.success) {
        // Update local state immediately for better UX
        setLocalLeadAccess(prev => prev.filter(id => id !== leadId));
        // Refresh campaigns data
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      } else {
        setError(result.error || "Failed to remove lead from campaign");
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove lead from campaign");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[var(--card)] rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div>
            <h2 className="text-xl font-semibold text-[var(--foreground)]">
              Manage Lead Access
            </h2>
            <p className="text-sm text-[var(--foreground-muted)] mt-1">
              Campaign: {campaignName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
          >
            <HiOutlineX className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-[var(--border)]">
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search leads by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {leadsLoading ? (
            <div className="p-6">
              <LoadingState message="Loading leads..." />
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Leads with Access */}
              <div>
                <h3 className="text-lg font-medium text-[var(--foreground)] mb-3">
                  Leads with Access ({leadsWithAccess.length})
                </h3>
                {leadsWithAccess.length === 0 ? (
                  <p className="text-[var(--foreground-muted)] text-sm">
                    No leads currently have access to this campaign.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {leadsWithAccess.map((lead) => (
                      <div
                        key={lead.id}
                        className="flex items-center justify-between p-3 bg-[var(--muted)] rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-[var(--foreground)]">
                            {lead.fullname}
                          </div>
                          <div className="text-sm text-[var(--foreground-muted)]">
                            {lead.email} • {lead.phone}
                          </div>
                          <div className="text-xs text-[var(--foreground-muted)]">
                            {lead.company}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveLead(lead.id)}
                          disabled={isLoading}
                          className="ml-3 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Remove Access"
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Leads without Access */}
              <div>
                <h3 className="text-lg font-medium text-[var(--foreground)] mb-3">
                  Available Leads ({leadsWithoutAccess.length})
                </h3>
                {leadsWithoutAccess.length === 0 ? (
                  <p className="text-[var(--foreground-muted)] text-sm">
                    {searchQuery
                      ? "No leads match your search criteria."
                      : "All leads already have access to this campaign."}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {leadsWithoutAccess.map((lead) => (
                      <div
                        key={lead.id}
                        className="flex items-center justify-between p-3 border border-[var(--border)] rounded-lg hover:bg-[var(--muted)] transition-colors"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-[var(--foreground)]">
                            {lead.fullname}
                          </div>
                          <div className="text-sm text-[var(--foreground-muted)]">
                            {lead.email} • {lead.phone}
                          </div>
                          <div className="text-xs text-[var(--foreground-muted)]">
                            {lead.company}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddLead(lead.id)}
                          disabled={isLoading}
                          className="ml-3 p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Grant Access"
                        >
                          <HiOutlinePlus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-[var(--border)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--foreground)] bg-[var(--muted)] hover:bg-[var(--muted)]/80 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignLeadAccessModal;